import { type RefObject, useEffect, useRef } from 'react';
import { Swiper as SwiperType } from 'swiper/types';

export interface UseOnPrevClickOptions {
  swiperRef: RefObject<SwiperType | null>;
  navigationEnabled: boolean;
  isSwiperReady: boolean;
  onPrevClick?: () => void;
}

/**
 * Capture-phase prev-chevron click so `onPrevClick` always fires. Only suppresses
 * Swiper's own handler when Swiper itself would short-circuit, so side-effect
 * consumers don't block normal slide-back.
 */
export function useOnPrevClick({
  swiperRef,
  navigationEnabled,
  isSwiperReady,
  onPrevClick,
}: UseOnPrevClickOptions): boolean {
  const onPrevClickRef = useRef(onPrevClick);
  useEffect(() => {
    onPrevClickRef.current = onPrevClick;
  }, [onPrevClick]);

  const hasOnPrevClick = Boolean(onPrevClick);

  useEffect(() => {
    if (!navigationEnabled || !hasOnPrevClick || !isSwiperReady) return;
    const prevEl = swiperRef.current?.navigation?.prevEl as HTMLElement | null | undefined;
    if (!prevEl) return;

    const handler = (event: Event): void => {
      const swiper = swiperRef.current;
      const wouldShortCircuit =
        !swiper || !swiper.allowSlidePrev || (swiper.isBeginning && !swiper.params.loop);
      if (wouldShortCircuit) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      onPrevClickRef.current?.();
    };
    prevEl.addEventListener('click', handler, true);
    return () => prevEl.removeEventListener('click', handler, true);
  }, [navigationEnabled, hasOnPrevClick, isSwiperReady, swiperRef]);

  return hasOnPrevClick;
}
